/**
 * Spec Phase 11 Slice 11C — admin-only cost indicator.
 *
 * Self-isolating: on mount calls /auth/me to determine the
 * caller's role. Only renders the cost indicator when role >=
 * admin. The hosting page (SpecHeader) can drop this in without
 * worrying about role plumbing.
 *
 * Drafters never see LLM spend — per the user's instruction.
 * The cost cap still fires for everyone (the section-generate
 * endpoint enforces it), but the 429 message strips the dollar
 * amount for non-admins.
 *
 * Cap thresholds (per brief §5):
 *   - 80% of cap → amber
 *   - 100% of cap → red, "cap reached" tooltip
 *   - otherwise → zinc
 */
import React, { useEffect, useState } from 'react';
import { DollarSign } from 'lucide-react';
import {
  getMe,
  getSpecTelemetry,
  type SpecTelemetryResponse,
} from '@/api/client';

interface Props {
  matterId: string;
  /** Optional click target — e.g. open the diagnostic panel's
   * Spec → Telemetry subtab. Caller passes whatever navigation
   * makes sense; the component only invokes the callback. */
  onClick?: () => void;
}

const _REFRESH_INTERVAL_MS = 60_000;

export default function CostIndicator({ matterId, onClick }: Props): JSX.Element | null {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [telemetry, setTelemetry] = useState<SpecTelemetryResponse | null>(null);

  // Role probe (once per mount).
  useEffect(() => {
    let cancelled = false;
    getMe().then((me) => {
      if (cancelled) return;
      const admin = me.authenticated && me.user?.role === 'admin';
      setIsAdmin(!!admin);
    }).catch(() => {
      if (!cancelled) setIsAdmin(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Telemetry poll (only if admin).
  useEffect(() => {
    if (!isAdmin || !matterId) return;
    let cancelled = false;
    let timer: number | null = null;

    const fetchOnce = async (): Promise<void> => {
      try {
        const data = await getSpecTelemetry(matterId);
        if (!cancelled) setTelemetry(data);
      } catch {
        // 403 (lost admin) or network — clear silently.
        if (!cancelled) setTelemetry(null);
      }
    };
    void fetchOnce();
    timer = window.setInterval(fetchOnce, _REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [isAdmin, matterId]);

  if (!isAdmin || !telemetry) return null;

  const spend = telemetry.cost.estimated_cost_usd;
  const cap = telemetry.cap.usd;
  const calls = telemetry.cost.total_calls;
  const ratio = cap > 0 ? spend / cap : 0;

  let cls = 'text-zinc-600 hover:text-zinc-900';
  let tooltip =
    `Spec LLM spend (last ${telemetry.cap.window_hours}h): `
    + `$${spend.toFixed(2)} of $${cap.toFixed(2)} cap`;
  if (ratio >= 1.0) {
    cls = 'text-rose-700 hover:text-rose-900';
    tooltip = (
      `Cap reached. New generates will be blocked. `
      + `Cap resets ${telemetry.cap.window_start.slice(0, 16)}.`
    );
  } else if (ratio >= 0.8) {
    cls = 'text-amber-700 hover:text-amber-900';
    tooltip += ' (approaching cap)';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-7 items-center gap-1 rounded-sm border border-transparent px-2 text-[11px] font-mono ${cls}`}
      data-testid="spec-cost-indicator"
      title={tooltip}
    >
      <DollarSign className="h-3 w-3" />
      {spend.toFixed(2)} ({calls} calls)
    </button>
  );
}
